// src/pages/CreatePaymentPage.js - Enhanced with Minimal Employee API
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, MenuItem, Stack, Button, CardContent,
  Avatar, Paper
} from '@mui/material';
import { AccountCircle } from '@mui/icons-material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createPayment } from '../slices/financeSlice';
import { useLocation } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import { fetchBuses } from '../slices/busesSlice';
import { fetchStaffMinimal } from '../slices/staffSlice'; // Use minimal API
import { fetchSchoolYears } from '../slices/schoolYearsSlice';
import { fetchAuthorizedPayers } from '../slices/authorizedPayersSlice';
import BankBranchSelect from '../components/BankBranchSelect';
import EntitySelect from '../components/EntitySelect';

const MotionPaper = motion(Paper);

const paymentTypes = [
  {
    "id": "uuid-1",
    "name": "cash",
    "display_name": "نقداً",
    "type": "cash"
  },
  {
    "id": "uuid-2",
    "name": "bank_transfer",
    "display_name": "تحويل بنكي",
    "type": "bank_transfer"
  },
  {
    "id": "uuid-3",
    "name": "cheque",
    "display_name": "شيك",
    "type": "cheque"
  },
  {
    "id": "uuid-4",
    "name": "visa",
    "display_name": "فيزا",
    "type": "visa"
  }
];

const entityTypes = ['موظف', 'باص', 'جهة صرف معتمدة', 'متفرقات'];

const CreatePaymentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { showToast } = useToast();
  
  // Redux selectors - Use minimal staff data
  const payments = useSelector((state) => state.payments || []);
  const authorizedPayers = useSelector((state) => state.authorizedPayers.items);
  const { minimal: staffMinimal, minimalStatus } = useSelector((state) => state.staff);
  const buses = useSelector((state) => state.buses.list);
  const activeYearId = useSelector(state => state.schoolYears?.list?.filter(year => year.is_active)[0]?.id || null);
  
  useEffect(() => {
    dispatch(fetchStaffMinimal({ is_archived: false })); // Use minimal API
    dispatch(fetchBuses());
    dispatch(fetchSchoolYears());
    dispatch(fetchAuthorizedPayers());
  }, [dispatch]);
  
  // Enhanced entity options with minimal staff data
  const entityOptions = {
    موظف: staffMinimal.filter(s => !s.is_archived).map((emp) => ({
      id: emp.id,
      name: emp.full_name, // Use pre-computed full_name from minimal data
      employee_id: emp.employee_id,
    })),
    باص: buses.map((bus) => ({
      id: bus.id,
      name: bus.name || bus.bus_number || 'باص غير معرف',
    })),
    'جهة صرف معتمدة': authorizedPayers.map((payer) => ({
      id: payer.id,
      name: payer.display_value || payer.name,
    })),
    متفرقات: [
      { id: 'misc1', name: 'شاي' },
      { id: 'misc2', name: 'قهوة' },
      { id: 'misc3', name: 'مستلزمات أخرى' },
    ],
  };

  const [isCreating, setIsCreating] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [form, setForm] = useState({
    receiptNumber: Date.now().toString(),
    entityType: location.state?.entityType || '',
    targetName: location.state?.targetName || '',
    targetId: location.state?.targetId || '',
    method: '',
    paymentTypeId: '',
    amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    type: '',
    reason: '',
    chequeDetails: {
      chequeNumber: '',
      dueDate: '',
      bankNumber: '',
      branchNumber: '',
      bankName: '',
      branchName: '',
      countryCode: '',
      bankId: null,
      branchId: null,
      accountNumber: '',
      chequeImage: null,
    },
    visaDetails: {
      receiptNumber: '',
      notes: '',
    },
  });

  const [previousPayments, setPreviousPayments] = useState([]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setAttachment(file);
      setForm((prev) => ({
        ...prev,
        chequeDetails: {
          ...prev.chequeDetails,
          chequeImage: file,
        },
      }));
    }
  };

  const handleChequeChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      chequeDetails: {
        ...prev.chequeDetails,
        [name]: value,
      },
    }));
  };

  const handleVisaChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      visaDetails: {
        ...prev.visaDetails,
        [name]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation
    if (!form.entityType) {
      showToast('يرجى اختيار نوع الجهة', 'error');
      return;
    }
    if (!form.targetName && form.entityType !== 'متفرقات') {
      showToast('يرجى اختيار اسم الجهة', 'error');
      return;
    }
    if (!form.method) {
      showToast('يرجى اختيار طريقة الدفع', 'error');
      return;
    }
    if (!form.amount || parseFloat(form.amount) <= 0) {
      showToast('يرجى إدخال مبلغ صحيح', 'error');
      return;
    }

    setIsCreating(true);
    
    const paymentData = {
      ...form,
      chequeDetails: form.method === 'شيك' ? form.chequeDetails : {},
      visaDetails: form.method === 'فيزا' ? form.visaDetails : {},
      proofAttachment: attachment || null,
      school_year: activeYearId
    };

    try {
      const resultAction = await dispatch(createPayment(paymentData));
      if (createPayment.rejected.match(resultAction)) {
        throw resultAction.error;
      }

      showToast('تم حفظ السند بنجاح', 'success');
      navigate('/financial');
    } catch (err) {
      showToast('حدث خطأ أثناء حفظ السند', 'error');
    } finally {
      setIsCreating(false);
    }
  };

  // Get selected employee details for display
  const selectedEmployee = form.entityType === 'موظف' 
    ? entityOptions.موظف.find(emp => emp.id === form.targetId)
    : null;

  // 🎨 Design System Applied
  const fieldSx = {
    fontFamily: 'Cairo, Tajawal, sans-serif',
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: '#fff',
      fontFamily: 'Cairo, Tajawal, sans-serif',
      '& fieldset': { borderColor: '#E5E7EB' },
      '&:hover fieldset': { borderColor: '#4338CA' },
      '&.Mui-focused fieldset': { borderColor: '#4338CA' },
    },
    '& .MuiInputLabel-root': {
      fontFamily: 'Cairo, Tajawal, sans-serif',
      color: '#6B7280',
      '&.Mui-focused': { color: '#4338CA' },
    },
  };

  const sectionLabelSx = {
    fontWeight: 700,
    color: '#1E1B4B',
    fontFamily: 'Cairo, Tajawal, sans-serif',
    mb: 1.5,
  };

  return (
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        {/* Header Section */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ReceiptLongIcon sx={{ color: '#fff', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                fontWeight={800}
                color="#fff"
                sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
              >
                سند صرف جديد
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  mt: 0.25,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              >
                إنشاء سند صرف مالي جديد وتحديد الجهة وطريقة الدفع
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="outlined"
            startIcon={<ArrowForwardIcon />}
            onClick={() => navigate('/financial')}
            sx={{
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.6)',
              px: 3,
              py: 1.1,
              borderRadius: 2.5,
              fontWeight: 700,
              textTransform: 'none',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
            }}
          >
            رجوع
          </Button>
        </MotionPaper>

        <form onSubmit={handleSubmit}>
          <MotionPaper
            component={motion.div}
            elevation={0}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
            sx={{
              p: { xs: 2, sm: 3 },
              mb: 3,
              borderRadius: 3,
              bgcolor: '#fff',
              boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
              border: '1px solid #E5E7EB',
            }}
          >
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              <Typography variant="subtitle1" sx={sectionLabelSx}>
                بيانات الجهة
              </Typography>
              <Stack spacing={3} dir="rtl">
              <TextField
                label="نوع الجهة"
                name="entityType"
                value={form.entityType}
                onChange={handleChange}
                select
                fullWidth
                sx={fieldSx}
              >
                {entityTypes.map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </TextField>

              {/* Enhanced Entity Selection with Search */}
              {form.entityType && form.entityType !== 'متفرقات' && (
                <EntitySelect
                  entityType={form.entityType}
                  options={entityOptions[form.entityType]}
                  targetId={form.targetId}
                  loading={form.entityType === 'موظف' && minimalStatus === 'loading'}
                  disabled={!!location.state?.targetId}
                  onChange={(entity) => {
                    setForm((prev) => ({
                      ...prev,
                      targetName: entity?.name || '',
                      targetId: entity?.id || '',
                    }));
                  }}
                  fieldSx={fieldSx}
                />
              )}

              {/* Selected Employee Preview */}
              {selectedEmployee && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2.5,
                    bgcolor: '#EEF2FF',
                    border: '1px solid #E5E7EB',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ color: '#4338CA', fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif', mb: 1 }}>
                    الموظف المختار:
                  </Typography>
                  <Stack useFlexGap direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: '#4338CA' }}>
                      <AccountCircle />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif', color: '#1E1B4B' }}>
                        {selectedEmployee.name}
                      </Typography>
                      {selectedEmployee.employee_id && (
                        <Typography variant="caption" sx={{ fontFamily: 'Cairo, Tajawal, sans-serif', color: '#6B7280' }}>
                          رقم الموظف: {selectedEmployee.employee_id}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Paper>
              )}
              </Stack>

              <Typography variant="subtitle1" sx={{ ...sectionLabelSx, mt: 4 }}>
                تفاصيل الدفع
              </Typography>
              <Stack spacing={3} dir="rtl">
              <TextField
                label="طريقة الدفع"
                name="method"
                value={form.method}
                onChange={(e) => {
                  const displayName = e.target.value;
                  const selectedType = paymentTypes.find((pt) => pt.display_name === displayName);
                  setForm((prev) => ({
                    ...prev,
                    method: displayName,
                    paymentTypeId: selectedType?.display_name || '',
                  }));
                }}
                select
                fullWidth
                sx={fieldSx}
              >
                {paymentTypes.map((pt) => (
                  <MenuItem key={pt.id} value={pt.display_name}>
                    {pt.display_name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="تاريخ الدفع"
                name="paymentDate"
                type="date"
                value={form.paymentDate}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={fieldSx}
              />

              {form.method === 'شيك' && (
                <Stack spacing={2}>
                  <TextField
                    label="المبلغ"
                    name="amount"
                    value={form.amount}
                    onChange={handleChange}
                    fullWidth
                    sx={fieldSx}
                  />
                  <TextField
                    label="تاريخ استحقاق الشيك"
                    type="date"
                    name="dueDate"
                    value={form.chequeDetails.dueDate}
                    onChange={handleChequeChange}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={fieldSx}
                  />
                  <TextField
                    label="رقم الشيك"
                    name="chequeNumber"
                    value={form.chequeDetails.chequeNumber}
                    onChange={handleChequeChange}
                    fullWidth
                    sx={fieldSx}
                  />
                  <BankBranchSelect
                    fieldSx={fieldSx}
                    bankId={form.chequeDetails.bankId}
                    bankName={form.chequeDetails.bankName}
                    branchId={form.chequeDetails.branchId}
                    branchName={form.chequeDetails.branchName}
                    onChange={(patch) =>
                      setForm((prev) => ({
                        ...prev,
                        chequeDetails: { ...prev.chequeDetails, ...patch },
                      }))
                    }
                  />
                  <TextField
                    label="رقم الحساب"
                    name="accountNumber"
                    value={form.chequeDetails.accountNumber}
                    onChange={handleChequeChange}
                    fullWidth
                    sx={fieldSx}
                  />

                  <Button
                    variant="outlined"
                    component="label"
                    sx={{
                      borderRadius: 2.5,
                      fontWeight: 700,
                      textTransform: 'none',
                      fontFamily: 'Cairo, Tajawal, sans-serif',
                      borderColor: '#4338CA',
                      color: '#4338CA',
                      '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' },
                    }}
                  >
                    رفع صورة الشيك
                    <input type="file" hidden accept="image/*,.pdf" onChange={handleFileChange} />
                  </Button>
                  {attachment && (
                    <Typography variant="body2" sx={{ color: '#4338CA', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                      ملف محدد: {attachment.name}
                    </Typography>
                  )}
                </Stack>
              )}

              {form.method === 'فيزا' && (
                <Stack spacing={2}>
                  <TextField
                    label="المبلغ"
                    name="amount"
                    value={form.amount}
                    onChange={handleChange}
                    fullWidth
                    sx={fieldSx}
                  />
                  <TextField
                    label="رقم إيصال الفيزا"
                    name="receiptNumber"
                    value={form.visaDetails.receiptNumber}
                    onChange={handleVisaChange}
                    fullWidth
                    sx={fieldSx}
                    placeholder="أدخل رقم إيصال الفيزا"
                  />
                  <TextField
                    label="ملاحظات الفيزا"
                    name="notes"
                    value={form.visaDetails.notes}
                    onChange={handleVisaChange}
                    fullWidth
                    multiline
                    rows={3}
                    sx={fieldSx}
                    placeholder="ملاحظات إضافية للدفع بالفيزا"
                  />
                </Stack>
              )}

              {form.method !== 'شيك' && form.method !== 'فيزا' && (
                <TextField
                  label="المبلغ"
                  name="amount"
                  value={form.amount}
                  onChange={handleChange}
                  fullWidth
                  sx={fieldSx}
                />
              )}

              <TextField
                label="سبب الصرف"
                name="reason"
                value={form.reason}
                onChange={handleChange}
                fullWidth
                sx={fieldSx}
              />
            </Stack>

            <Stack useFlexGap direction="row" spacing={2} justifyContent="center" mt={4}>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  bgcolor: '#4338CA',
                  color: '#fff',
                  px: 4,
                  py: 1.1,
                  borderRadius: 2.5,
                  boxShadow: 'none',
                  fontWeight: 700,
                  textTransform: 'none',
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
                  '&.Mui-disabled': { bgcolor: '#C7D2FE', color: '#fff' },
                }}
                disabled={isCreating || (form.entityType === 'موظف' && minimalStatus === 'loading')}
              >
                {isCreating ? 'جاري الحفظ...' : 'حفظ السند'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/financial')}
                sx={{
                  px: 4,
                  py: 1.1,
                  borderRadius: 2.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  borderColor: '#DC2626',
                  color: '#DC2626',
                  '&:hover': { borderColor: '#DC2626', bgcolor: '#FEE2E2' },
                }}
              >
                إلغاء
              </Button>
            </Stack>
            </CardContent>
          </MotionPaper>
        </form>
      </Box>
    </Box>
  );
};

export default CreatePaymentPage;